import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useGenericExportBulkAction,
  type IBulkActionItem,
} from "@ryde/controls";
import {
  useDropdownSourceContext,
  useHubRideActionConfirm,
  useModalConfirm,
  useModalConfirmsRide,
  useRidesBulkActions,
  useRidesListActions,
} from "@ryde/redux";
import {
  bulkCloneFormDrawerAF,
  bulkJourneyAssignFormDrawerAF,
  bulkOperationsDrawerAF,
  bulkRebuildRideDrawerAtom,
  bulkRebuildRoutesCustomDrawerAtom,
  bulkRecalculatePriceOptionsDrawerAF,
  bulkRecalculateSupplierPriceOptionsDrawerAF,
  bulkSelectedControlsAtomFamily,
  bulkUpdateFormDrawerAF,
  bulkUpdateStationsDrawerAtom,
  ConfirmResult,
  DrawerType,
  hasPastTaxiSupplierRide,
  HubRideActionKind,
  Log,
  noop,
  RideCrudModeKey,
  RideDeleteActionsResultEnum,
  selectedRidesPassengersIdsSelector,
  selectedRidesSelector,
  selectedTableRowsIdsAtomFamily,
  systemSettingsDtoAtom,
  useDrawers,
  type RydeUiUnit,
} from "@ryde/shared";

import { RIDE_BULK_PRINT } from "./bulk-operations.rides.constants";
import { buildRideBulkAction } from "./bulk-operations.rides.utils";
import type { IRidePrintLabels } from "./bulk-print.rides.models";
import { buildRidePrintLabels } from "./bulk-print.rides.utils";

const RIDE_ENTITY: RydeUiUnit = RideCrudModeKey.uiUnit;

// The print component is mounted hidden first, so give React a tick to render it before printing.
const PRINT_RENDER_DELAY_MS = 100;

export const useBulkOperationsRidesActions = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const { openDrawer } = useDrawers();
  const [printLabels, setPrintLabels] = useState<IRidePrintLabels>();

  const selectedRideIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(RIDE_ENTITY),
  );
  const selectedRides = useRecoilValue(selectedRidesSelector);
  const [bulkSelectedFields, setBulkSelectedFields] = useRecoilState(
    bulkSelectedControlsAtomFamily(RIDE_ENTITY),
  );

  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF(RIDE_ENTITY),
  );

  const setBulkUpdateFormDrawerOpened = useSetRecoilState(
    bulkUpdateFormDrawerAF(RIDE_ENTITY),
  );

  const setBulkCloneFormDrawerOpened = useSetRecoilState(
    bulkCloneFormDrawerAF(RIDE_ENTITY),
  );

  const setBulkJourneyAssignFormDrawerOpened = useSetRecoilState(
    bulkJourneyAssignFormDrawerAF(RIDE_ENTITY),
  );

  const selectedRidesPassengersIds = useRecoilValue(
    selectedRidesPassengersIdsSelector,
  );

  const setBulkRecalculatePriceOptionsDrawerOpened = useSetRecoilState(
    bulkRecalculatePriceOptionsDrawerAF(RIDE_ENTITY),
  );

  const setBulkRecalculateSupplierPriceOptionsDrawerOpened = useSetRecoilState(
    bulkRecalculateSupplierPriceOptionsDrawerAF(RIDE_ENTITY),
  );

  const setBulkRebuildRideDrawerOpened = useSetRecoilState(
    bulkRebuildRideDrawerAtom,
  );

  const setBulkRebuildRoutesCustomDrawerOpened = useSetRecoilState(
    bulkRebuildRoutesCustomDrawerAtom,
  );

  const setBulkUpdateStationsDrawerOpened = useSetRecoilState(
    bulkUpdateStationsDrawerAtom,
  );

  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const enableSupplierPriceRates =
    systemSettingsDto?.metadata?.enableSupplierPriceRates;

  const {
    bulkArchive,
    bulkCancel,
    bulkReject,
    bulkRebuildRoutes,
    bulkSendToSupplier,
    bulkFitVehicleAndRecalculate,
    bulkSendSMS,
    bulkFetchTrackingHistory,
  } = useRidesBulkActions();

  const { confirmRideArchiveAction, confirmRideCancelAction } =
    useModalConfirmsRide();
  const { confirmSelectedHubRidesAction } = useHubRideActionConfirm();
  const { rides } = useRidesListActions();
  const genericExportAction = useGenericExportBulkAction();
  const { getDropdownItems } = useDropdownSourceContext();

  const printComponentRef = useRef<HTMLDivElement | null>(null);
  const isPrintPendingRef = useRef(false);

  const handlePrintBulkRides = useReactToPrint({
    contentRef: printComponentRef,
    preserveAfterPrint: false,
  }) as (content?: unknown) => void;

  const handlePrintClick = useEvent(async () => {
    // Held until the print is invoked: a second click while labels load or during the delay prints nothing more.
    if (isPrintPendingRef.current) {
      return;
    }
    isPrintPendingRef.current = true;

    const isFlightsEnabled = Boolean(
      systemSettingsDto?.metadata?.enableFlights,
    );
    // The snapshot is taken after a fixed delay, so the labels go in as props rather than loading after mount.
    const [areaItems, flightItems] = await Promise.allSettled([
      getDropdownItems(RIDE_BULK_PRINT.dropdownSource.area),
      isFlightsEnabled
        ? getDropdownItems(RIDE_BULK_PRINT.dropdownSource.flight)
        : Promise.resolve([]),
    ]);

    setPrintLabels(
      buildRidePrintLabels({ areaItems, flightItems, isFlightsEnabled }),
    );

    setTimeout(() => {
      try {
        handlePrintBulkRides();
      } finally {
        isPrintPendingRef.current = false;
      }
    }, PRINT_RENDER_DELAY_MS);
  });

  const handleUpdateClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkUpdateFormDrawerOpened?.(true);

    if (bulkSelectedFields?.length) {
      setBulkSelectedFields([]);
    }
  });

  const handleCloneClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkCloneFormDrawerOpened?.(true);
  });

  const handleJourneyAssignClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkJourneyAssignFormDrawerOpened?.(true);
  });

  // Same panel the passengers page bulk action opens, addressed to everyone riding the selected rides.
  const handleSendToPassengersClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    openDrawer<DrawerType.BulkPassengersMessage>({
      drawerProps: {
        type: DrawerType.BulkPassengersMessage,
        selectedPassengersIds: selectedRidesPassengersIds,
        hasRecipientsDerivedFromRides: true,
      },
    });
  });

  const handleArchiveClick = useEvent(async () => {
    // Hub paired-ride warning first (same order as single-ride drawer), then booking-status dialog.
    const isHubArchiveConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides,
      actionKind: HubRideActionKind.Archive,
    });

    if (!isHubArchiveConfirmed) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_ARCHIVE_CANCELED`),
        selectedRideIds,
      );
      return;
    }

    const archiveRidesAction = await confirmRideArchiveAction();

    if (archiveRidesAction === RideDeleteActionsResultEnum.NoAction) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_ARCHIVE_CANCELED`),
        selectedRideIds,
      );

      return;
    }

    bulkArchive(selectedRideIds, archiveRidesAction);
  });

  const handleCancelClick = useEvent(async () => {
    const isHubCancelConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides,
      actionKind: HubRideActionKind.Cancel,
    });

    if (!isHubCancelConfirmed) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_CANCELLATION_CANCELED`),
        selectedRideIds,
      );
      return;
    }

    // RYDE-7633: a past taxi-supplier ride in the selection is canceled locally (no provider
    // notification), and the backend requires the operator's reason as its only audit trail.
    const { bookingAction: cancelRidesAction, cancelationReason } =
      await confirmRideCancelAction({
        requireCancelationReason: hasPastTaxiSupplierRide(selectedRides),
      });

    if (cancelRidesAction === RideDeleteActionsResultEnum.NoAction) {
      Log.warnAndToast(
        t(`general.RIDES_ACTIONS_WARNINGS.BULK_CANCELLATION_CANCELED`),
        selectedRideIds,
      );

      return;
    }

    bulkCancel(selectedRideIds, cancelRidesAction, cancelationReason);
  });

  const handleRecalculatePriceClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkRecalculatePriceOptionsDrawerOpened(true);
  });

  const handleRecalculateSupplierPriceClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkRecalculateSupplierPriceOptionsDrawerOpened(true);
  });

  const handleRebuildRideClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkRebuildRideDrawerOpened(true);
  });

  const handleUpdateStationsClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkUpdateStationsDrawerOpened(true);
  });

  const handleFitVehicleAndRecalculateClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.FIT_VEHICLE_AND_RECALCULATE_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const result = await bulkFitVehicleAndRecalculate(selectedRideIds);

      if (!result?.success) {
        Log.warnAndToast(
          t(
            `general.RIDES_ACTIONS_WARNINGS.BULK_FIT_VEHICLE_AND_RECALCULATE_CANCELED`,
          ),
          selectedRideIds,
        );
      }
    }
  });

  const handleRejectClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.REJECT_RIDES_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkReject(selectedRideIds);
    }
  });

  const handleSendToSupplierClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.SEND_TO_SUPPLIER_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkSendToSupplier(selectedRideIds);
    }
  });

  const handleRebuildRouteClick = useEvent(async () => {
    const isHubModifyConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides,
      actionKind: HubRideActionKind.Modify,
    });

    if (!isHubModifyConfirmed) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.REBUILD_ROUTES_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkRebuildRoutes(selectedRideIds);
    }
  });

  const handleRebuildRoutesCustomClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkRebuildRoutesCustomDrawerOpened(true);
  });

  const handleSendSMS = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`ride.CONFIRM_SEND_SMS`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      await bulkSendSMS(selectedRideIds);
    }
  });

  const handleFetchTrackingHistoryClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.FETCH_TRACKING_HISTORY_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      await bulkFetchTrackingHistory(selectedRideIds);
    }
  });

  const actions: IBulkActionItem[] = [
    buildRideBulkAction({ t, key: "archive", onClick: handleArchiveClick }),
    buildRideBulkAction({
      t,
      key: "sendToSupplier",
      onClick: handleSendToSupplierClick,
    }),
    buildRideBulkAction({ t, key: "reject", onClick: handleRejectClick }),
    buildRideBulkAction({ t, key: "cancel", onClick: handleCancelClick }),
    buildRideBulkAction({
      t,
      key: "recalculatePrice",
      onClick: handleRecalculatePriceClick,
    }),
    buildRideBulkAction({
      t,
      key: "recalculateSupplierPrice",
      onClick: handleRecalculateSupplierPriceClick,
      isHidden: !enableSupplierPriceRates,
    }),
    buildRideBulkAction({
      t,
      key: "rebuildRoutes",
      onClick: handleRebuildRouteClick,
    }),
    buildRideBulkAction({
      t,
      key: "rebuildRoutesCustom",
      onClick: handleRebuildRoutesCustomClick,
    }),
    buildRideBulkAction({
      t,
      key: "rebuildRide",
      onClick: handleRebuildRideClick,
    }),
    buildRideBulkAction({
      t,
      key: "updateStations",
      onClick: handleUpdateStationsClick,
    }),
    buildRideBulkAction({
      t,
      key: "fitVehicleAndRecalculate",
      onClick: handleFitVehicleAndRecalculateClick,
    }),
    buildRideBulkAction({
      t,
      key: "sendMessages",
      onClick: handleSendToPassengersClick,
    }),
    buildRideBulkAction({ t, key: "sendSms", onClick: handleSendSMS }),
    buildRideBulkAction({
      t,
      key: "fetchTrackingHistory",
      onClick: handleFetchTrackingHistoryClick,
    }),
    buildRideBulkAction({
      t,
      key: "duplicateRides",
      onClick: handleCloneClick,
    }),
    buildRideBulkAction({
      t,
      key: "assignJourney",
      onClick: handleJourneyAssignClick,
    }),
    buildRideBulkAction({ t, key: "updateRides", onClick: handleUpdateClick }),
    buildRideBulkAction({
      t,
      key: "printExportReports",
      onClick: handlePrintClick,
    }),
    genericExportAction,
    buildRideBulkAction({ t, key: "addReturnRide", onClick: noop }),
    buildRideBulkAction({ t, key: "approve", onClick: noop }),
  ];

  return { actions, printComponentRef, printLabels };
};
